// Vision attachments for the builder chat.
//
// Files dropped / pasted / picked here are NOT uploaded to Drupal — they are
// forwarded to the model as content blocks for a single turn (a maquette to
// recreate as a block, a screenshot to inspect, a PDF spec to read).
//
// Images are downscaled client-side to Anthropic's recommended max edge
// (~1568px) before base64 encoding: the model resizes anything larger anyway,
// so doing it here cuts upload bandwidth and input tokens. PDFs are sent as-is
// under a size cap.
//
// Trois rendus par image, et chacun a sa raison d'être :
//   - `data`     — le plein format, pour le fil. Jamais persisté.
//   - `viewUrl`  — la copie CONSULTABLE, celle que la visionneuse ouvre. Une
//                  vignette de 128 px agrandie est illisible : sans cette copie
//                  il n'y a rien à ouvrir une fois le message envoyé.
//   - `thumbUrl` — la vignette de la bulle.
// `viewUrl` et `thumbUrl` partent en IndexedDB avec le message ; le plein
// format, lui, n'y va pas — c'est ce qui borne le poids d'une conversation.

export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif']
export const ACCEPTED_TYPES = [...ACCEPTED_IMAGE_TYPES, 'application/pdf']
export const ACCEPT_ATTR = ACCEPTED_TYPES.join(',')

// Anthropic downsizes images past ~1568px on the long edge; match that.
const MAX_IMAGE_EDGE = 1568
// Copie consultable : assez grande pour relire une maquette en plein écran,
// assez petite pour être réécrite en IndexedDB à chaque message du tour.
const VIEW_EDGE = 1280
const THUMB_EDGE = 128
// Keep us under Drupal's post_max_size and the API's 32MB/PDF limit, with margin.
const MAX_PDF_BYTES = 20 * 1024 * 1024
// Cap the source image before downscaling (decode-bomb guard).
const MAX_IMAGE_BYTES = 30 * 1024 * 1024

let idSeq = 0
const nextId = () => `att-${Date.now().toString(36)}-${idSeq++}`

export function isAccepted(file) {
  return !!file && ACCEPTED_TYPES.includes(file.type)
}

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader()
    fr.onload = () => resolve(fr.result)
    fr.onerror = () => reject(fr.error ?? new Error('Lecture impossible'))
    fr.readAsDataURL(file)
  })
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Image illisible'))
    img.src = src
  })
}

// Redimensionne un bitmap DÉJÀ décodé : la longue arête ≤ maxEdge, jamais
// agrandi. `flatten` peint un fond blanc sous l'image — indispensable avant un
// encodage JPEG, qui n'a pas de canal alpha (sans lui, le transparent vire au
// noir).
function render(img, maxEdge, outType, { flatten = false } = {}) {
  const longest = Math.max(img.width, img.height) || 1
  const scale = longest > maxEdge ? maxEdge / longest : 1
  const w = Math.max(1, Math.round(img.width * scale))
  const h = Math.max(1, Math.round(img.height * scale))
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (flatten) {
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, w, h)
  }
  ctx.drawImage(img, 0, 0, w, h)
  return canvas.toDataURL(outType, 0.85)
}

// Les trois rendus, à partir d'UN seul décodage. GIFs inchangés (un canevas
// aplatirait l'animation, et ces fichiers sont déjà petits).
async function renditions(dataUrl, type) {
  if (type === 'image/gif') {
    return { mediaType: type, full: dataUrl, view: dataUrl, thumb: dataUrl }
  }
  const img = await loadImage(dataUrl)
  // Keep PNG transparency; re-encode everything else as JPEG (smaller).
  const outType = type === 'image/png' ? 'image/png' : 'image/jpeg'
  return {
    mediaType: outType,
    full: render(img, MAX_IMAGE_EDGE, outType),
    // TOUJOURS en JPEG, celle-là : un PNG de 1280 px pèse plusieurs mégaoctets
    // une fois en base64, et cette copie est écrite en base à chaque message.
    view: render(img, VIEW_EDGE, 'image/jpeg', { flatten: true }),
    thumb: render(img, THUMB_EDGE, outType),
  }
}

const stripPrefix = (dataUrl) => dataUrl.slice(dataUrl.indexOf(',') + 1)

/**
 * Read a File into an attachment object:
 *   { id, name, kind: 'image'|'pdf', mediaType, size, data, viewUrl, thumbUrl }
 * `data` is raw base64 (no data: prefix) for the wire; `viewUrl` et `thumbUrl`
 * sont des data URL d'affichage (`null` pour un PDF). Throws (French message)
 * on reject.
 */
export async function readFileAsAttachment(file) {
  if (!isAccepted(file)) {
    throw new Error(`Type non supporté : ${file.name || file.type}`)
  }
  const isPdf = file.type === 'application/pdf'

  if (isPdf) {
    if (file.size > MAX_PDF_BYTES) {
      throw new Error(`PDF trop volumineux (max ${Math.round(MAX_PDF_BYTES / 1048576)} Mo) : ${file.name}`)
    }
    const dataUrl = await readAsDataURL(file)
    return {
      id: nextId(),
      name: file.name,
      kind: 'pdf',
      mediaType: 'application/pdf',
      size: file.size,
      data: stripPrefix(dataUrl),
      viewUrl: null,
      thumbUrl: null,
    }
  }

  if (file.size > MAX_IMAGE_BYTES) {
    throw new Error(`Image trop volumineuse : ${file.name}`)
  }
  const sourceUrl = await readAsDataURL(file)
  const out = await renditions(sourceUrl, file.type)
  return {
    id: nextId(),
    name: file.name,
    kind: 'image',
    mediaType: out.mediaType,
    size: file.size,
    data: stripPrefix(out.full),
    viewUrl: out.view,
    thumbUrl: out.thumb,
  }
}

/**
 * Celles qu'on peut OUVRIR en grand, dans l'ordre où elles sont affichées.
 *
 * Une conversation enregistrée AVANT `viewUrl` n'a que sa vignette de 128 px :
 * agrandie elle serait illisible — ces pièces jointes-là restent de simples
 * images, sans promesse d'ouverture. Un PDF n'est pas persisté du tout.
 */
export function openableAttachments(list) {
  return (list ?? []).filter(a => a?.kind === 'image' && a.viewUrl)
}

// What the sidecar consumes — only what the model needs.
export function toWireAttachment(att) {
  return { name: att.name, mediaType: att.mediaType, data: att.data }
}

// What we persist on the user message — drops the heavy full-size base64, garde
// la copie consultable : c'est elle, et elle seule, qui rend une pièce jointe
// ouvrable une fois le message parti (et après un rechargement).
export function toDisplayAttachment(att) {
  return {
    id: att.id,
    name: att.name,
    kind: att.kind,
    mediaType: att.mediaType,
    viewUrl: att.viewUrl ?? null,
    thumbUrl: att.thumbUrl,
  }
}
